export interface FleetOptimisationInputs {
  currentFleet: number;
  optimizedFleet: number;
  baselineDailyKm: number;
  optimizedDailyKm: number;
  costPerKm: number;
  annualFixedCost: number;
  baselineCo2GramsPerKm: number;
  optimizedCo2GramsPerKm: number;
  baselineCapexPerVehicle: number;
  baselineVehiclesWithCapex: number;
  optimizedCapexPerVehicle: number;
  optimizedVehiclesWithCapex: number;
  analysisYears: number;
  baselineCapexEnabled: boolean;
  optimizedCapexEnabled: boolean;
  baselineDriverHoursEnabled: boolean;
  optimizedDriverHoursEnabled: boolean;
  baselineDriverHoursPerVehicle: number;
  optimizedDriverHoursPerVehicle: number;
  driverCostPerHour: number;
}

export type FleetOptimisationBooleanInputKey =
  'baselineCapexEnabled'
  | 'optimizedCapexEnabled'
  | 'baselineDriverHoursEnabled'
  | 'optimizedDriverHoursEnabled';
export type FleetOptimisationNumericInputKey = Exclude<keyof FleetOptimisationInputs, FleetOptimisationBooleanInputKey>;

export type FleetOptimisationFormValues =
  Record<FleetOptimisationNumericInputKey, string> & Record<FleetOptimisationBooleanInputKey, boolean>;

export type FleetOptimisationValidationErrors =
  Partial<Record<Exclude<keyof FleetOptimisationFormValues, FleetOptimisationBooleanInputKey>, string>>;

export interface FleetOptimisationResult {
  baselineCost: number;
  optimizedCost: number;
  totalBaselineCost: number;
  totalOptimizedCost: number;
  financialSavings: number;
  totalFinancialSavings: number;
  baselineAnnualKm: number;
  optimizedAnnualKm: number;
  baselineCo2Tonnes: number;
  optimizedCo2Tonnes: number;
  totalBaselineCo2Tonnes: number;
  totalOptimizedCo2Tonnes: number;
  co2ReductionTonnes: number;
  totalCo2ReductionTonnes: number;
  co2ReductionPercentage: number | null;
  totalCo2ReductionPercentage: number | null;
  baselineCapexAnnualAddition: number;
  baselineCapexIncluded: boolean;
  optimizedCapexAnnualAddition: number;
  optimizedCapexIncluded: boolean;
  baselineDriverCost: number;
  optimizedDriverCost: number;
}

export const DEFAULT_FLEET_OPTIMISATION_VALUES: FleetOptimisationFormValues = {
  currentFleet: '50',
  optimizedFleet: '42',
  baselineDailyKm: '180',
  optimizedDailyKm: '195',
  costPerKm: '0.3',
  annualFixedCost: '8000',
  baselineCo2GramsPerKm: '1100',
  optimizedCo2GramsPerKm: '1100',
  baselineCapexEnabled: false,
  optimizedCapexEnabled: false,
  baselineCapexPerVehicle: '60000',
  baselineVehiclesWithCapex: '50',
  optimizedCapexPerVehicle: '60000',
  optimizedVehiclesWithCapex: '42',
  analysisYears: '1',
  baselineDriverHoursEnabled: false,
  optimizedDriverHoursEnabled: false,
  baselineDriverHoursPerVehicle: '8',
  optimizedDriverHoursPerVehicle: '8',
  driverCostPerHour: '30',
};

const DAYS_PER_YEAR = 365;
const REQUIRED_ERROR = 'Enter a valid non-negative number';
const POSITIVE_INTEGER_ERROR = 'Enter a whole number of at least 1';
const BASE_NUMERIC_FIELDS: Array<FleetOptimisationNumericInputKey> = [
  'currentFleet',
  'optimizedFleet',
  'baselineDailyKm',
  'optimizedDailyKm',
  'costPerKm',
  'annualFixedCost',
  'baselineCo2GramsPerKm',
  'optimizedCo2GramsPerKm',
  'analysisYears',
];

function parseNonNegativeNumber(value: string): number | null {
  if (value.trim() === '') {
    return null;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return null;
  }
  return parsed;
}

function parseOptionalNonNegativeNumber(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') {
    return 0;
  }
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return null;
  }
  return parsed;
}

function parsePositiveInteger(value: string): number | null {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) {
    return null;
  }
  return parsed;
}

export function parseFleetOptimisationInputs(
  values: FleetOptimisationFormValues
): {
  isValid: boolean;
  inputs: FleetOptimisationInputs | null;
  errors: FleetOptimisationValidationErrors;
} {
  const errors: FleetOptimisationValidationErrors = {};
  const parsedValues: Partial<FleetOptimisationInputs> = {
    baselineCapexEnabled: values.baselineCapexEnabled,
    optimizedCapexEnabled: values.optimizedCapexEnabled,
    baselineDriverHoursEnabled: values.baselineDriverHoursEnabled,
    optimizedDriverHoursEnabled: values.optimizedDriverHoursEnabled,
  };

  BASE_NUMERIC_FIELDS.forEach((fieldName) => {
    const parsed = parseNonNegativeNumber(values[fieldName]);
    if (parsed === null) {
    errors[fieldName] = REQUIRED_ERROR;
      return;
    }
    parsedValues[fieldName] = parsed;
  });

  const analysisYears = parsePositiveInteger(values.analysisYears);
  if (analysisYears === null) {
    errors.analysisYears = POSITIVE_INTEGER_ERROR;
  } else {
    parsedValues.analysisYears = analysisYears;
  }

  if (values.baselineCapexEnabled) {
    const baselineCapexPerVehicle = parseNonNegativeNumber(values.baselineCapexPerVehicle);
    if (baselineCapexPerVehicle === null) {
      errors.baselineCapexPerVehicle = REQUIRED_ERROR;
    } else {
      parsedValues.baselineCapexPerVehicle = baselineCapexPerVehicle;
    }

    const baselineVehiclesWithCapex = parseNonNegativeNumber(values.baselineVehiclesWithCapex);
    if (baselineVehiclesWithCapex === null) {
      errors.baselineVehiclesWithCapex = REQUIRED_ERROR;
    } else if (parsedValues.currentFleet !== undefined && baselineVehiclesWithCapex > parsedValues.currentFleet) {
      errors.baselineVehiclesWithCapex = 'Vehicles with CAPEX must be less than or equal to Current Fleet Size';
    } else {
      parsedValues.baselineVehiclesWithCapex = baselineVehiclesWithCapex;
    }
  } else {
    parsedValues.baselineCapexPerVehicle = parseNonNegativeNumber(values.baselineCapexPerVehicle) ?? 0;
    parsedValues.baselineVehiclesWithCapex = parseNonNegativeNumber(values.baselineVehiclesWithCapex) ?? 0;
  }

  if (values.optimizedCapexEnabled) {
    const optimizedCapexPerVehicle = parseNonNegativeNumber(values.optimizedCapexPerVehicle);
    if (optimizedCapexPerVehicle === null) {
      errors.optimizedCapexPerVehicle = REQUIRED_ERROR;
    } else {
      parsedValues.optimizedCapexPerVehicle = optimizedCapexPerVehicle;
    }

    const optimizedVehiclesWithCapex = parseNonNegativeNumber(values.optimizedVehiclesWithCapex);
    if (optimizedVehiclesWithCapex === null) {
      errors.optimizedVehiclesWithCapex = REQUIRED_ERROR;
    } else if (parsedValues.optimizedFleet !== undefined && optimizedVehiclesWithCapex > parsedValues.optimizedFleet) {
      errors.optimizedVehiclesWithCapex = 'Vehicles with CAPEX must be less than or equal to Optimized Fleet Size';
    } else {
      parsedValues.optimizedVehiclesWithCapex = optimizedVehiclesWithCapex;
    }
  } else {
    parsedValues.optimizedCapexPerVehicle = parseNonNegativeNumber(values.optimizedCapexPerVehicle) ?? 0;
    parsedValues.optimizedVehiclesWithCapex = parseNonNegativeNumber(values.optimizedVehiclesWithCapex) ?? 0;
  }

  if (values.baselineDriverHoursEnabled) {
    const driverCostPerHour = parseOptionalNonNegativeNumber(values.driverCostPerHour);
    if (driverCostPerHour === null) {
      errors.driverCostPerHour = REQUIRED_ERROR;
    } else {
      parsedValues.driverCostPerHour = driverCostPerHour;
    }
    const baselineDriverHoursPerVehicle = parseNonNegativeNumber(values.baselineDriverHoursPerVehicle);
    if (baselineDriverHoursPerVehicle === null) {
      errors.baselineDriverHoursPerVehicle = REQUIRED_ERROR;
    } else {
      parsedValues.baselineDriverHoursPerVehicle = baselineDriverHoursPerVehicle;
    }
  } else {
    parsedValues.baselineDriverHoursPerVehicle = parseNonNegativeNumber(values.baselineDriverHoursPerVehicle) ?? 0;
  }

  if (values.optimizedDriverHoursEnabled) {
    const driverCostPerHour = parseOptionalNonNegativeNumber(values.driverCostPerHour);
    if (driverCostPerHour === null) {
      errors.driverCostPerHour = REQUIRED_ERROR;
    } else if (parsedValues.driverCostPerHour === undefined) {
      parsedValues.driverCostPerHour = driverCostPerHour;
    }
    const optimizedDriverHoursPerVehicle = parseNonNegativeNumber(values.optimizedDriverHoursPerVehicle);
    if (optimizedDriverHoursPerVehicle === null) {
      errors.optimizedDriverHoursPerVehicle = REQUIRED_ERROR;
    } else {
      parsedValues.optimizedDriverHoursPerVehicle = optimizedDriverHoursPerVehicle;
    }
  } else {
    parsedValues.optimizedDriverHoursPerVehicle = parseNonNegativeNumber(values.optimizedDriverHoursPerVehicle) ?? 0;
  }

  if (!values.baselineDriverHoursEnabled && !values.optimizedDriverHoursEnabled) {
    parsedValues.driverCostPerHour = parseNonNegativeNumber(values.driverCostPerHour) ?? 0;
  }

  const isValid = Object.keys(errors).length === 0;
  return {
    isValid,
    inputs: isValid ? (parsedValues as FleetOptimisationInputs) : null,
    errors,
  };
}

export function calculateFleetOptimisation(inputs: FleetOptimisationInputs): FleetOptimisationResult {
  const baselineAnnualKm = inputs.currentFleet * inputs.baselineDailyKm * DAYS_PER_YEAR;
  const optimizedAnnualKm = inputs.optimizedFleet * inputs.optimizedDailyKm * DAYS_PER_YEAR;

  const baselineCapexAnnualAddition = inputs.baselineCapexEnabled
    ? (inputs.baselineCapexPerVehicle * inputs.baselineVehiclesWithCapex) / inputs.analysisYears
    : 0;
  const optimizedCapexAnnualAddition = inputs.optimizedCapexEnabled
    ? (inputs.optimizedCapexPerVehicle * inputs.optimizedVehiclesWithCapex) / inputs.analysisYears
    : 0;
  const baselineDriverCost = inputs.baselineDriverHoursEnabled
    ? inputs.currentFleet * inputs.baselineDriverHoursPerVehicle * DAYS_PER_YEAR * inputs.driverCostPerHour
    : 0;
  const optimizedDriverCost = inputs.optimizedDriverHoursEnabled
    ? inputs.optimizedFleet * inputs.optimizedDriverHoursPerVehicle * DAYS_PER_YEAR * inputs.driverCostPerHour
    : 0;
  const baselineCost = (baselineAnnualKm * inputs.costPerKm)
    + (inputs.currentFleet * inputs.annualFixedCost)
    + baselineCapexAnnualAddition
    + baselineDriverCost;
  const optimizedCost = (optimizedAnnualKm * inputs.costPerKm)
    + (inputs.optimizedFleet * inputs.annualFixedCost)
    + optimizedCapexAnnualAddition
    + optimizedDriverCost;
  const totalBaselineCost = baselineCost * inputs.analysisYears;
  const totalOptimizedCost = optimizedCost * inputs.analysisYears;
  const financialSavings = baselineCost - optimizedCost;

  const baselineCo2Tonnes = (baselineAnnualKm * inputs.baselineCo2GramsPerKm) / 1000000;
  const optimizedCo2Tonnes = (optimizedAnnualKm * inputs.optimizedCo2GramsPerKm) / 1000000;
  const co2ReductionTonnes = baselineCo2Tonnes - optimizedCo2Tonnes;
  const totalBaselineCo2Tonnes = baselineCo2Tonnes * inputs.analysisYears;
  const totalOptimizedCo2Tonnes = optimizedCo2Tonnes * inputs.analysisYears;
  const totalFinancialSavings = financialSavings * inputs.analysisYears;
  const totalCo2ReductionTonnes = co2ReductionTonnes * inputs.analysisYears;
  const co2ReductionPercentage = baselineCo2Tonnes > 0
    ? (co2ReductionTonnes / baselineCo2Tonnes) * 100
    : null;
  const totalCo2ReductionPercentage = totalBaselineCo2Tonnes > 0
    ? (totalCo2ReductionTonnes / totalBaselineCo2Tonnes) * 100
    : null;

  return {
    baselineCost,
    optimizedCost,
    totalBaselineCost,
    totalOptimizedCost,
    financialSavings,
    totalFinancialSavings,
    baselineAnnualKm,
    optimizedAnnualKm,
    baselineCo2Tonnes,
    optimizedCo2Tonnes,
    totalBaselineCo2Tonnes,
    totalOptimizedCo2Tonnes,
    co2ReductionTonnes,
    totalCo2ReductionTonnes,
    co2ReductionPercentage,
    totalCo2ReductionPercentage,
    baselineCapexAnnualAddition,
    baselineCapexIncluded: inputs.baselineCapexEnabled && baselineCapexAnnualAddition > 0,
    optimizedCapexAnnualAddition,
    optimizedCapexIncluded: inputs.optimizedCapexEnabled && optimizedCapexAnnualAddition > 0,
    baselineDriverCost,
    optimizedDriverCost,
  };
}
